/** Playback belongs to the visible campaign, including responsive media overrides. */
class CampaignMedia extends HTMLElement {
  #video;
  #playButton;
  #muteButton;
  #controller;
  #slide;
  #viewport;
  #intersection;
  #slideObserver;
  #intersecting = false;
  #active = false;
  #userPaused = false;
  #resumeWhenVisible = false;

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#playButton = this.querySelector('[data-video-play]');
    this.#muteButton = this.querySelector('[data-video-mute]');
    this.#playButton?.addEventListener('click', this.#togglePlayback, { signal });
    this.#muteButton?.addEventListener('click', this.#toggleMute, { signal });
    for (const event of ['play', 'pause', 'ended', 'volumechange']) {
      this.#video.addEventListener(event, this.#updateControls, { signal });
    }
    this.#updateControls();
    this.#video.controls = false;
    this.dataset.enhanced = 'true';
    this.#slide = this.closest('slideshow-slide');
    this.#viewport = matchMedia('(max-width: 749px)');
    this.#viewport.addEventListener('change', this.#sync);
    document.addEventListener('visibilitychange', this.#sync);
    this.#intersection = new IntersectionObserver(([entry]) => {
      this.#intersecting = entry.isIntersecting;
      this.#sync();
    });
    this.#intersection.observe(this);
    if (this.#slide) {
      this.#slideObserver = new MutationObserver(this.#sync);
      this.#slideObserver.observe(this.#slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  #sync = () => {
    const active = this.#intersecting && !document.hidden &&
      this.#slide?.getAttribute('aria-hidden') !== 'true' && this.#video.getClientRects().length > 0;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = typeof navigator !== 'undefined' && navigator.connection?.saveData;
    if (!active) {
      if (this.#active && !this.#video.paused && !this.#video.ended) this.#resumeWhenVisible = true;
      this.#video.pause();
    } else if (!this.#active && !this.#userPaused &&
      (this.#resumeWhenVisible || (this.dataset.autoplay === 'true' && !reducedMotion && !saveData))) {
      // The play button remains available if the browser disallows autoplay.
      this.#video.play().catch(() => {});
      this.#resumeWhenVisible = false;
    }
    this.#active = active;
  };

  #togglePlayback = () => {
    this.#userPaused = !this.#video.paused && !this.#video.ended;
    this.#resumeWhenVisible = false;
    if (this.#userPaused) this.#video.pause();
    else this.#video.play().catch(() => {});
  };

  #toggleMute = () => {
    this.#video.muted = !this.#video.muted;
    this.#updateControls();
  };

  #updateControls = () => {
    const playing = !this.#video.paused && !this.#video.ended;
    this.dataset.playing = String(playing);
    this.dataset.muted = String(this.#video.muted);
    if (this.#playButton) {
      this.#playButton.disabled = false;
      this.#playButton.setAttribute('aria-label', playing ? this.#playButton.dataset.pauseLabel : this.#playButton.dataset.playLabel);
    }
    if (this.#muteButton) {
      this.#muteButton.disabled = false;
      this.#muteButton.setAttribute('aria-label', this.#video.muted ? this.#muteButton.dataset.unmuteLabel : this.#muteButton.dataset.muteLabel);
    }
  };

  disconnectedCallback() {
    this.#controller?.abort();
    this.#video?.pause();
    this.#intersection?.disconnect();
    this.#slideObserver?.disconnect();
    this.#viewport?.removeEventListener('change', this.#sync);
    document.removeEventListener('visibilitychange', this.#sync);
    this.#active = false;
    this.#intersecting = false;
    this.#resumeWhenVisible = false;
  }
}
if (!customElements.get('sc-campaign-media')) customElements.define('sc-campaign-media', CampaignMedia);
